// src/hooks/usePanelApi.tsx

import { API } from 'aws-amplify'
import {
  useState,
  useCallback,
  createContext,
  useEffect,
  useContext,
  useRef,
  useMemo,
  ReactNode,
  SetStateAction,
  Dispatch,
  MutableRefObject,
} from 'react'

import { DashboardId, PanelId, PanelPool } from '@/types/domain'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

type PanelContextValue = {
  data: any[]
  map: Record<string, any[]>
  setMap: Dispatch<SetStateAction<Record<string, any[]>>>
  invalidate: (_id: string | number) => void
  inflightRef: MutableRefObject<Map<string, Promise<any[]>>>
}

const PanelCtx = createContext<PanelContextValue | null>(null)

const fetchPanelsRaw = async (id: DashboardId) => {
  const API_NAME = 'karmaApi'
  const endpoint = `/dashboard/${id}/panel/`
  const options = { method: 'GET', headers: { ...authHeader() } }

  const result = await API.get(API_NAME, endpoint, options).catch(error => {
    const { response } = error || {}
    const { data, status } = response || {}
    const { message } = data || {}
    if (
      status === 400 &&
      typeof message === 'string' &&
      message.includes('Bad Request')
    ) {
      const sensor = message.split('for')[1]?.replace(/.$/, '')?.trim()
      return { factorError: true, sensor }
    }
    throw error
  })

  if (!result || !result.data) return []
  return result.data.sort(
    (a, b) => a.attributes.position - b.attributes.position
  )
}

type Props = {
  children: ReactNode
  id?: DashboardId
}

export const PanelDataProvider = ({ children, id }: Props) => {
  const [data, setData] = useState([])
  const [map, setMap] = useState({})
  const inflightRef = useRef(new Map())
  const mapRef = useRef(map)
  const { start, stop } = usePreloader()

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  useEffect(() => {
    mapRef.current = map
  }, [map])

  useEffect(() => {
    let active = true

    const run = async () => {
      if (!id) return

      try {
        const key = String(id)
        const cached = mapRef.current[key]

        if (Array.isArray(cached)) {
          if (active) setData(cached)
          return
        }

        let p = inflightRef.current.get(key)
        if (!p) {
          const exec = async () => {
            try {
              return await fetchPanelsRaw(id)
            } catch (err) {
              const status = err?.status ?? err?.response?.status
              if (status === 401 || status === 403) {
                await refreshToken()
                return await fetchPanelsRaw(id)
              }
              throw err
            }
          }

          p = withPreloader(exec)
            .then(result => {
              setMap(prev => ({ ...prev, [key]: result }))
              return result
            })
            .finally(() => inflightRef.current.delete(key))

          inflightRef.current.set(key, p)
        }

        const result = await p
        if (active) setData(result || [])
      } catch (error) {
        if (active) notifyError(`PanelApi1: ${error?.message}`)
      }
    }

    run()

    return () => {
      active = false
    }
  }, [id, withPreloader])

  const invalidate = useCallback(id => {
    const key = String(id)
    inflightRef.current.delete(key)
    setMap(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ data, map, setMap, invalidate, inflightRef }),
    [data, map, invalidate]
  )

  return <PanelCtx.Provider value={value}>{children}</PanelCtx.Provider>
}

export const usePanelApi = () => {
  const { start, stop } = usePreloader()
  const ctx = useContext(PanelCtx)
  if (!ctx) throw new Error('PanelCtx missing')

  const { map, setMap, invalidate, inflightRef } = ctx
  const mapRef = useRef(map)

  useEffect(() => {
    mapRef.current = map
  }, [map])

  const withRequest = useCallback(async fn => {
    try {
      await refreshToken()
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          notifyError(`PanelApi2: ${e2?.message}`)
          return null
        }
      }
      notifyError(`PanelApi3: ${error?.message}`)
      return null
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getPanels = useCallback(
    (id: DashboardId, silent = false): Promise<PanelPool> => {
      if (!id || id <= 0) return Promise.resolve([])

      const key = String(id)
      const cached = mapRef.current?.[key]
      if (Array.isArray(cached)) return Promise.resolve(cached)

      const inflight = inflightRef?.current?.get(key)
      if (inflight) return inflight

      const exec = () =>
        withRequest(async () => {
          const res = await fetchPanelsRaw(id)
          setMap(prev => ({ ...prev, [key]: res }))
          return res
        })

      const p = (silent ? exec() : withPreloader(exec))
        .then(res => (Array.isArray(res) ? res : []))
        .finally(() => inflightRef?.current?.delete(key))

      inflightRef?.current?.set(key, p)
      return p
    },
    [withRequest, setMap, inflightRef, withPreloader]
  )

  const move = useCallback(
    (id: DashboardId, pId: PanelId, { old_position, new_position }) =>
      withPreloader(() =>
        withRequest(async () => {
          const body = { data: { attributes: { old_position, new_position } } }
          const url = `${REACT_APP_API_URL}/dashboard/${id}/panel/${pId}/move`
          const options = {
            body: JSON.stringify(body),
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            method: 'PATCH',
          }

          const response = await fetch(url, options)
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }
          await response.json().catch(() => null)
          invalidate(id)
          notifySuccess(LC.PANEL_MOVED)
          getPanels(id, true).catch(() => null)
          return true
        })
      ),
    [withRequest, invalidate, withPreloader, getPanels]
  )

  const remove = useCallback(
    (id: DashboardId, pId: PanelId) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) throw new Error(LC.DASHBOARD_ID_REQUIRED)
          if (!pId) throw new Error(LC.PANEL_ID_REQUIRED)

          const url = `${REACT_APP_API_URL}/dashboard/${id}/panel/${pId}/`
          const options = { headers: { ...authHeader() }, method: 'DELETE' }
          const response = await fetch(url, options)

          if (!response.ok) {
            const text = await response.text()
            const error = {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }

            console.error(
              'expected error as mentioned in KAR-2189. backend removes the panel but fires response code (500 instead of 2xx) with message: system error in DBD015.at this point NO error is thrown until backend is fixed'
            )
            console.error('PanelApi.remove Bypass:', error)
            // throw error
          }

          await response.json().catch(() => null)

          invalidate(id)
          notifySuccess(LC.PANEL_DELETED)
          getPanels(id, true).catch(() => null)

          return true
        })
      ),
    [getPanels, invalidate, withPreloader, withRequest]
  )

  const save = useCallback(
    data =>
      withPreloader(() =>
        withRequest(async () => {
          const { name, dashboard_id, sensor_ids = [] } = data || {}
          const _sensor_ids = Array.from(
            new Set(sensor_ids.map(v => Number(v)))
          ).filter(Number.isFinite)

          if (!name) throw new Error(LC.PANEL_NAME_REQUIRED)
          if (sensor_ids.length === 0)
            throw new Error(LC.PANEL_SENSORS_REQUIRED)

          const {
            position,
            indicator,
            graphic_type,
            start_date,
            end_date,
            granularity,
            presentation_level,
          } = data || {}

          const body = {
            data: {
              attributes: {
                dashboard_id,
                name,
                position,
                indicator,
                graphic_type,
                start_date,
                end_date,
                granularity,
                presentation_level,
              },
              sensor_ids: _sensor_ids,
            },
          }

          const url = `${REACT_APP_API_URL}/dashboard/${dashboard_id}/panel`
          const options = {
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            method: 'POST',
            body: JSON.stringify(body),
          }
          const response = await fetch(url, options)
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }
          await response.json().catch(() => null)
          invalidate(dashboard_id)
          notifySuccess(LC.PANEL_SAVED)

          return true
        })
      ),
    [withRequest, invalidate, withPreloader]
  )

  const update = useCallback(
    data =>
      withPreloader(() =>
        withRequest(async () => {
          const { name, dashboard_id, entity_id, sensor_ids } = data || {}
          const _sensor_ids = Array.from(
            new Set(sensor_ids.map(v => Number(v)))
          ).filter(Number.isFinite)

          if (!name) throw new Error(LC.PANEL_NAME_REQUIRED)
          if (!dashboard_id) throw new Error('Dashboard ID erforderlich')
          if (!entity_id) throw new Error('Entity ID erforderlich')

          const {
            position,
            indicator,
            graphic_type,
            start_date,
            end_date,
            granularity,
            presentation_level,
          } = data || {}

          const body = {
            data: {
              attributes: {
                dashboard_id,
                name,
                position,
                indicator,
                graphic_type,
                start_date,
                end_date,
                granularity,
                presentation_level,
              },
              sensor_ids: _sensor_ids,
            },
          }

          const url = `${REACT_APP_API_URL}/dashboard/${dashboard_id}/panel/${entity_id}/`
          const options = {
            method: 'PATCH',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }

          const response = await fetch(url, options)
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }

          await response.json().catch(() => null)
          invalidate(dashboard_id)
          notifySuccess(LC.PANEL_UPDATED)
          getPanels(dashboard_id, true).catch(() => null)
          return true
        })
      ),
    [withRequest, invalidate, withPreloader, getPanels]
  )

  const updatePanels = useCallback(
    (id: DashboardId, data) =>
      withPreloader(() =>
        withRequest(async () => {
          const body = {
            data: {
              attributes: {
                start_date: data.start_date,
                end_date: data.end_date,
                granularity: data.granularity,
              },
            },
          }

          const url = `${REACT_APP_API_URL}/dashboard/${id}/panel/`
          const options = {
            method: 'PATCH',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }

          const response = await fetch(url, options)
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }
          await response.json().catch(() => null)
          invalidate(id)
          notifySuccess('Panels aktualisiert')
          getPanels(id, true).catch(() => null)
          return true
        })
      ),
    [withRequest, invalidate, withPreloader, getPanels]
  )

  return {
    getPanels,
    move,
    remove,
    save,
    update,
    updatePanels,
    invalidate,
  }
}
